import { reactive } from 'vue';

import { store } from '@/decorators/store';
import { Attachment } from '@/entities/Attachment';
import { AbstractEntityStore } from '@/stores/AbstractEntityStore';

@store({ id: 'AttachmentStore' })
export class AttachmentStore extends AbstractEntityStore<Attachment> {
  protected entityIdKey = 'attachmentID';
  protected entityLabelKey = 'filename';
  protected entityValueKey = 'attachmentID';

  protected recurringAttachments = reactive<Map<number, Attachment>>(new Map());

  protected uiState = reactive({
    downloading: new Set<number>(),
    deleting: new Set<number>(),
  });

  get recurringAttachmentList(): Attachment[] {
    return Array.from(this.recurringAttachments.values());
  }

  setRecurringAttachments(attachments: Attachment[]): void {
    this.recurringAttachments.clear();
    attachments.forEach((attachment) => this.addRecurringAttachment(attachment));
  }

  addRecurringAttachment(attachment: Attachment): void {
    this.recurringAttachments.set(attachment.attachmentID, attachment);
  }

  removeRecurringAttachment(attachmentID: number): void {
    this.recurringAttachments.delete(attachmentID);
  }

  getByTransportId(transportId: string): Attachment[] {
    return this.entities.filter((attachment) => attachment.id === transportId);
  }

  setDownloading(id: number, isDownloading: boolean): void {
    if (isDownloading) {
      this.uiState.downloading.add(id);
    }
    else {
      this.uiState.downloading.delete(id);
    }
  }

  setDeleting(id: number, isDeleting: boolean): void {
    if (isDeleting) {
      this.uiState.deleting.add(id);
    }
    else {
      this.uiState.deleting.delete(id);
    }
  }

  isDownloading(id: number): boolean {
    return this.uiState.downloading.has(id);
  }

  isDeleting(id: number): boolean {
    return this.uiState.deleting.has(id);
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  clearAllUIStates(): void {
    this.uiState.downloading.clear();
    this.uiState.deleting.clear();
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  getUIState(id: number): { isDownloading: boolean; isDeleting: boolean } {
    return {
      isDownloading: this.isDownloading(id),
      isDeleting: this.isDeleting(id),
    };
  }
}
